import { useEffect, useState, useCallback } from "react";
import { localToday } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Smile, Meh, Frown, Utensils, Bed, Baby, BookOpen, Send, Mail, MessageSquare, Plus, Sparkles, AlertTriangle, Camera, Download, Palette, StickyNote, X, Trash2 } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import MultiKindSchriftjeDialog, { countKnownKinderenInTranscript } from "@/components/MultiKindSchriftjeDialog";

const STEMMING_ICON = {
  blij: { icon: Smile, color: "text-emerald-600 bg-emerald-50", label: "Blij" },
  neutraal: { icon: Meh, color: "text-neutral-600 bg-neutral-100", label: "Neutraal" },
  verdrietig: { icon: Frown, color: "text-blue-600 bg-blue-50", label: "Verdrietig" },
  boos: { icon: Frown, color: "text-red-600 bg-red-50", label: "Boos" },
  moe: { icon: Meh, color: "text-amber-600 bg-amber-50", label: "Moe" },
};

export default function CommunicatiePage() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader
        title="Communicatie"
        subtitle="Schriftjes, nieuwsbrieven en mentorberichten — houd ouders en team op één lijn."
        testid="communicatie-header"
      />
      <Tabs defaultValue="schriftje" className="w-full">
        <TabsList className="bg-white border border-neutral-200">
          <TabsTrigger value="schriftje" data-testid="tab-schriftje"><BookOpen className="h-4 w-4 mr-2" /> Schriftje</TabsTrigger>
          <TabsTrigger value="nieuwsbrief" data-testid="tab-nieuwsbrief"><Mail className="h-4 w-4 mr-2" /> Nieuwsbrieven</TabsTrigger>
          <TabsTrigger value="mentor" data-testid="tab-mentor"><MessageSquare className="h-4 w-4 mr-2" /> Mentorberichten</TabsTrigger>
        </TabsList>
        <TabsContent value="schriftje" className="mt-6"><SchriftjeTab /></TabsContent>
        <TabsContent value="nieuwsbrief" className="mt-6"><NieuwsbriefTab /></TabsContent>
        <TabsContent value="mentor" className="mt-6"><MentorTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function SchriftjeTab() {
  const { user } = useAuth();
  const canWrite = can(user, "schriftje", "write");
  const [rows, setRows] = useState([]);
  const [kinderen, setKinderen] = useState([]);
  const [kindId, setKindId] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());

  // Deep-link vanuit Groepsapp: ?kind=<id> pre-selecteert en opent AI-schriftje dialog
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preKind = params.get("kind");
    if (preKind) {
      setKindId(preKind);
      setForm(f => ({ ...f, kind_id: preKind }));
      setOpen(true);
      // Clean URL zodat refresh de dialog niet heropent
      const url = new URL(window.location.href);
      url.searchParams.delete("kind");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (kindId !== "all") params.kind_id = kindId;
    const [s, k] = await Promise.all([api.get("/schriftje", { params }), api.get("/children")]);
    setRows(s.data); setKinderen(k.data);
  }, [kindId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/schriftje", { ...form, verschoning_luier: parseInt(form.verschoning_luier || 0) });
      toast.success("Schriftje opgeslagen"); setOpen(false); setForm(init()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const kindMap = Object.fromEntries(kinderen.map(k => [k.id, k]));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Select value={kindId} onValueChange={setKindId}>
          <SelectTrigger className="w-72" data-testid="schriftje-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            <SelectItem value="all">Alle kinderen</SelectItem>
            {kinderen.map(k => <SelectItem key={k.id} value={k.id}>{k.voornaam} {k.achternaam}</SelectItem>)}
          </SelectContent>
        </Select>
        {canWrite && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-schriftje-btn">
                <Plus className="h-4 w-4 mr-1" /> Nieuw schriftje
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">Nieuw schriftje-entry</DialogTitle>
                <DialogDescription>Deel de dag met ouders — of laat de AI-assistent een concept genereren op basis van je steekwoorden.</DialogDescription>
              </DialogHeader>
              <AIAssistBlock kinderen={kinderen} onFill={(nl, en) => setForm(f => ({
                ...f,
                activiteiten: nl || f.activiteiten,
                notitie_voor_ouder: en ? (f.notitie_voor_ouder ? f.notitie_voor_ouder + "\n\n---\nEnglish:\n" + en : "English:\n" + en) : f.notitie_voor_ouder,
              }))} defaultKindId={form.kind_id} />
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Kind</Label>
                  <Select value={form.kind_id} onValueChange={v => setForm({ ...form, kind_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Kies kind" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      {kinderen.map(k => <SelectItem key={k.id} value={k.id}>{k.voornaam} {k.achternaam}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Datum</Label><Input type="date" value={form.datum} onChange={e => setForm({ ...form, datum: e.target.value })} /></div>
                <div>
                  <Label>Stemming</Label>
                  <Select value={form.stemming} onValueChange={v => setForm({ ...form, stemming: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(STEMMING_ICON).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Activiteiten</Label><Textarea rows={3} value={form.activiteiten} onChange={e => setForm({ ...form, activiteiten: e.target.value })} data-testid="schriftje-activiteiten" /></div>
                <div><Label>Eten</Label><Input value={form.eten} onChange={e => setForm({ ...form, eten: e.target.value })} placeholder="Bijv. Alles opgegeten" /></div>
                <div><Label>Slaap</Label><Input value={form.slaap} onChange={e => setForm({ ...form, slaap: e.target.value })} placeholder="12:30 - 14:00" /></div>
                <div><Label>Verschoning (luier)</Label><Input type="number" min="0" max="10" value={form.verschoning_luier} onChange={e => setForm({ ...form, verschoning_luier: e.target.value })} /></div>
                <div className="col-span-2"><Label>Hoogtepunt van de dag</Label><Input value={form.hoogtepunt} onChange={e => setForm({ ...form, hoogtepunt: e.target.value })} /></div>
                <div className="col-span-2"><Label>Notitie voor ouder</Label><Textarea rows={2} value={form.notitie_voor_ouder} onChange={e => setForm({ ...form, notitie_voor_ouder: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.length === 0 && <div className="col-span-2 text-sm text-neutral-500 p-6">Nog geen schriftje-entries.</div>}
        {rows.map(s => {
          const k = kindMap[s.kind_id];
          const sc = STEMMING_ICON[s.stemming] || STEMMING_ICON.blij;
          const StI = sc.icon;
          return (
            <Card key={s.id} className="p-5 bg-white shadow-none" data-testid={`schriftje-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-medium text-neutral-800">{k ? `${k.voornaam} ${k.achternaam}` : "Kind"}</div>
                  <div className="text-xs text-neutral-500">{new Date(s.datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${sc.color}`}>
                  <StI className="h-5 w-5" />
                </div>
              </div>
              <p className="text-sm text-neutral-700 mb-3">{s.activiteiten}</p>
              <div className="grid grid-cols-3 gap-2 text-xs text-neutral-600">
                {s.eten && <div className="flex items-center gap-1"><Utensils className="h-3 w-3" /> {s.eten}</div>}
                {s.slaap && <div className="flex items-center gap-1"><Bed className="h-3 w-3" /> {s.slaap}</div>}
                {s.verschoning_luier > 0 && <div className="flex items-center gap-1"><Baby className="h-3 w-3" /> {s.verschoning_luier}x</div>}
              </div>
              {s.hoogtepunt && (
                <div className="mt-3 p-3 rounded-md bg-[color:var(--sage-100)]/50 border border-[color:var(--sage-100)]">
                  <div className="text-xs uppercase tracking-wider text-[color:var(--sage)] font-semibold mb-1">Hoogtepunt</div>
                  <div className="text-sm text-neutral-800">{s.hoogtepunt}</div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-400">{s.author_name}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function initSchriftje() {}
function init() {
  return { kind_id: "", datum: localToday(), stemming: "blij", activiteiten: "", eten: "", slaap: "", verschoning_luier: 0, hoogtepunt: "", notitie_voor_ouder: "" };
}

function NieuwsbriefTab() {
  const { user } = useAuth();
  const canWrite = can(user, "nieuwsbrief", "write");
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titel: "", body: "", location_ids: [] });

  const load = useCallback(async () => {
    const [n, l] = await Promise.all([api.get("/nieuwsbrief"), api.get("/locations")]);
    setRows(n.data); setLocations(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/nieuwsbrief", form); toast.success("Nieuwsbrief verzonden"); setOpen(false); setForm({ titel: "", body: "", location_ids: [] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {canWrite && (
        <div className="mb-4 flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--sage)" }} className="text-white" data-testid="add-nieuwsbrief-btn">
                <Send className="h-4 w-4 mr-1" /> Nieuwe nieuwsbrief
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-xl">
              <DialogHeader>
                <DialogTitle>Nieuwsbrief opstellen</DialogTitle>
                <DialogDescription>Laat leeg om organisatiebreed te sturen, of selecteer specifieke locaties.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label>Titel</Label><Input value={form.titel} onChange={e => setForm({ ...form, titel: e.target.value })} /></div>
                <div><Label>Bericht</Label><Textarea rows={8} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} /></div>
                <div>
                  <Label>Locaties (leeg = organisatiebreed)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {locations.map(l => {
                      const on = form.location_ids.includes(l.id);
                      return (
                        <button key={l.id} type="button"
                          onClick={() => setForm({ ...form, location_ids: on ? form.location_ids.filter(x => x !== l.id) : [...form.location_ids, l.id] })}
                          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${on ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]" : "border-neutral-200 hover:bg-neutral-100"}`}
                        >{l.name}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
                <Button onClick={submit} style={{ background: "var(--sage)" }} className="text-white">Verzenden</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      <div className="space-y-4">
        {rows.length === 0 && <div className="text-sm text-neutral-500 p-6">Nog geen nieuwsbrieven.</div>}
        {rows.map(n => (
          <Card key={n.id} className="p-6 bg-white shadow-none" data-testid={`nieuwsbrief-${n.id}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-display text-xl tracking-tight">{n.titel}</h3>
              <Badge variant="outline" className={n.location_ids?.length ? "" : "bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent"}>
                {n.location_ids?.length ? `${n.location_ids.length} locatie(s)` : "Organisatiebreed"}
              </Badge>
            </div>
            <div className="text-xs text-neutral-500 mb-3">{n.author_name} · {new Date(n.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed">{n.body}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MentorTab() {
  const { user } = useAuth();
  const canWrite = can(user, "mentorbericht", "write");
  const [kinderen, setKinderen] = useState([]);
  const [threads, setThreads] = useState({}); // kind_id → count unread
  const [activeKid, setActiveKid] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]); // base64 data URLs
  const [busy, setBusy] = useState(false);

  const loadKinderen = useCallback(async () => {
    try {
      const k = await api.get("/children");
      setKinderen(k.data);
      const m = await api.get("/mentorbericht");
      const perKid = {};
      m.data.forEach(msg => {
        if (msg.van_type === "ouder" && !msg.gelezen_door_team) {
          perKid[msg.kind_id] = (perKid[msg.kind_id] || 0) + 1;
        }
      });
      setThreads(perKid);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon kinderen niet laden");
    }
  }, []);
  useEffect(() => { loadKinderen(); }, [loadKinderen]);

  const loadThread = useCallback(async (kid) => {
    if (!kid) return;
    try {
      const r = await api.get(`/chat/${kid}`);
      setMessages(r.data.messages || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadThread(activeKid); }, [activeKid, loadThread]);

  // Real-time nieuwe berichten via WebSocket
  useEffect(() => {
    if (!activeKid) return;
    const handler = (e) => {
      const msg = e.detail?.message;
      if (msg && msg.kind_id === activeKid) {
        setMessages(prev => prev.some(x => x.id === msg.id) ? prev : [...prev, msg]);
      }
    };
    window.addEventListener("chat:new_message", handler);
    return () => window.removeEventListener("chat:new_message", handler);
  }, [activeKid]);

  const activeKid_ = kinderen.find(k => k.id === activeKid);

  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    for (const f of files) {
      if (f.size > 500_000) {
        toast.error(`${f.name} te groot (max ~500KB)`);
        continue;
      }
      try {
        const data = await readFile(f);
        setPhotos(p => [...p, data]);
      } catch { toast.error(`${f.name} kon niet worden geladen`); }
    }
    e.target.value = "";
  };

  const send = async (activity_type = null, activity_details = null, presetBody = null) => {
    if (!activeKid) return;
    const body = presetBody != null ? presetBody : text;
    if (!body.trim() && photos.length === 0 && !activity_type) return;
    setBusy(true);
    try {
      const payload = {
        kind_id: activeKid,
        body: body.trim(),
        type: activity_type ? "activity" : (photos.length > 0 ? "activity" : "text"),
        activity_type,
        activity_details,
        media: photos,
      };
      const r = await api.post("/mentorbericht", payload);
      setMessages(m => [...m, r.data]);
      setText("");
      setPhotos([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const QUICK_ACTIVITIES = [
    { key: "voeding", label: "Voeding", icon: Utensils, color: "bg-orange-100 text-orange-700" },
    { key: "slaap", label: "Slaap", icon: Bed, color: "bg-indigo-100 text-indigo-700" },
    { key: "luier", label: "Luier", icon: Baby, color: "bg-amber-100 text-amber-700" },
    { key: "stemming", label: "Stemming", icon: Smile, color: "bg-emerald-100 text-emerald-700" },
    { key: "activiteit", label: "Activiteit", icon: Palette, color: "bg-pink-100 text-pink-700" },
    { key: "notitie", label: "Notitie", icon: StickyNote, color: "bg-neutral-100 text-neutral-700" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4" data-testid="mentor-chat">
      {/* Kinderen lijst */}
      <div className="border border-neutral-200 rounded-lg bg-white overflow-hidden">
        <div className="p-3 border-b border-neutral-200 text-xs uppercase tracking-widest text-neutral-500 font-semibold">Kinderen</div>
        <div className="max-h-[65vh] overflow-y-auto">
          {kinderen.length === 0 && <div className="p-4 text-xs text-neutral-500">Geen kinderen.</div>}
          {kinderen.map(k => {
            const unread = threads[k.id] || 0;
            const active = activeKid === k.id;
            return (
              <button
                key={k.id}
                onClick={() => setActiveKid(k.id)}
                data-testid={`chat-kind-${k.id}`}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors ${active ? "bg-[color:var(--sage-100)]/40 border-l-2 border-l-[color:var(--sage)]" : ""}`}
              >
                <div className="w-8 h-8 rounded-full bg-[color:var(--sage-100)] flex items-center justify-center text-[10px] font-semibold text-[color:var(--sage)]">
                  {(k.voornaam?.[0] || "") + (k.achternaam?.[0] || "")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{k.voornaam} {k.achternaam}</div>
                </div>
                {unread > 0 && (
                  <span className="text-[10px] font-bold bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center" data-testid={`chat-unread-${k.id}`}>
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Chat thread */}
      <div className="border border-neutral-200 rounded-lg bg-white flex flex-col min-h-[65vh]" data-testid="chat-panel">
        {!activeKid && (
          <div className="flex-1 flex items-center justify-center text-sm text-neutral-500">
            <div className="text-center">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Kies een kind om de chat te openen.
            </div>
          </div>
        )}
        {activeKid && (
          <>
            <div className="p-3 border-b border-neutral-200 flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[color:var(--sage-100)] flex items-center justify-center text-xs font-semibold text-[color:var(--sage)]">
                {(activeKid_?.voornaam?.[0] || "") + (activeKid_?.achternaam?.[0] || "")}
              </div>
              <div>
                <div className="text-sm font-semibold">{activeKid_?.voornaam} {activeKid_?.achternaam}</div>
                <div className="text-[10px] text-neutral-500">Chat met de ouder — activiteiten worden direct verstuurd</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-thread">
              {messages.length === 0 && (
                <div className="text-center text-sm text-neutral-400 py-8">Nog geen berichten. Verstuur de eerste activiteit ⬇️</div>
              )}
              {messages.map(m => <MessageBubble key={m.id} message={m} mineType="medewerker" onDeleted={(mid) => setMessages(prev => prev.filter(x => x.id !== mid))} />)}
            </div>
            {canWrite && (
              <div className="border-t border-neutral-200 p-3 space-y-2" data-testid="chat-composer">
                {/* Activity quick-buttons */}
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_ACTIVITIES.map(a => (
                    <button
                      key={a.key}
                      type="button"
                      onClick={() => send(a.key, null, `${a.label}: ${text || "—"}`)}
                      disabled={busy}
                      data-testid={`activity-${a.key}`}
                      className={`text-[11px] rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${a.color} hover:opacity-80 transition-opacity disabled:opacity-50`}
                    >
                      <a.icon className="h-3 w-3" /> {a.label}
                    </button>
                  ))}
                </div>
                {/* Foto previews */}
                {photos.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={p} alt="" className="h-16 w-16 rounded object-cover border" />
                        <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                                className="absolute -top-1 -right-1 bg-white rounded-full border shadow p-0.5" data-testid={`photo-remove-${i}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Input row */}
                <div className="flex gap-2">
                  <label className="cursor-pointer inline-flex items-center justify-center h-9 px-2 border border-neutral-200 rounded hover:bg-neutral-50" title="Foto toevoegen (max 4)">
                    <Camera className="h-4 w-4" />
                    <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhotos} data-testid="chat-photo-input" disabled={photos.length >= 4} />
                  </label>
                  <Textarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Typ een bericht… (Enter = versturen, Shift+Enter = nieuwe regel)"
                    rows={1}
                    className="min-h-[38px] flex-1 resize-none text-sm"
                    data-testid="chat-input"
                  />
                  <Button onClick={() => send()} disabled={busy || (!text.trim() && photos.length === 0)}
                          data-testid="chat-send" style={{ background: "var(--sage)" }} className="text-white">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Gedeelde bubble-render voor leidster + ouder chat
export function MessageBubble({ message, mineType, onDeleted }) {
  const { user } = useAuth();
  const isMine = message.van_type === mineType;
  const canDelete = user?.role === "superadmin" || message.van_user_id === user?.id;
  const isActivity = message.type === "activity" && message.activity_type;
  const ICONS = {
    voeding: { icon: Utensils, color: "bg-orange-100 text-orange-700", label: "Voeding" },
    slaap: { icon: Bed, color: "bg-indigo-100 text-indigo-700", label: "Slaap" },
    luier: { icon: Baby, color: "bg-amber-100 text-amber-700", label: "Luier" },
    stemming: { icon: Smile, color: "bg-emerald-100 text-emerald-700", label: "Stemming" },
    activiteit: { icon: Palette, color: "bg-pink-100 text-pink-700", label: "Activiteit" },
    notitie: { icon: StickyNote, color: "bg-neutral-100 text-neutral-700", label: "Notitie" },
    foto: { icon: Camera, color: "bg-blue-100 text-blue-700", label: "Foto" },
  };
  const meta = isActivity ? ICONS[message.activity_type] : null;
  const downloadPhoto = (dataUrl, i) => {
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `chat-${message.id.slice(0, 8)}-${i + 1}.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const remove = async () => {
    if (!window.confirm("Bericht definitief verwijderen?")) return;
    try {
      await api.delete(`/mentorbericht/${message.id}`);
      toast.success("Bericht verwijderd");
      onDeleted?.(message.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} group`} data-testid={`msg-${message.id}`}>
      <div className={`relative max-w-[80%] rounded-2xl p-2.5 ${isMine ? "bg-[color:var(--sage-100)]/50 rounded-tr-sm" : "bg-neutral-100 rounded-tl-sm"}`}>
        {canDelete && (
          <button
            onClick={remove}
            data-testid={`msg-delete-${message.id}`}
            className="absolute -top-2 -right-2 bg-white rounded-full border shadow p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
            title="Bericht verwijderen"
          >
            <Trash2 className="h-3 w-3 text-red-600" />
          </button>
        )}
        {!isMine && (
          <div className="text-[10px] text-neutral-500 mb-0.5 px-0.5">{message.van_naam}</div>
        )}
        {meta && (
          <div className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 mb-1.5 ${meta.color}`}>
            <meta.icon className="h-3 w-3" /> {meta.label}
          </div>
        )}
        {message.body && <div className="text-sm text-neutral-800 whitespace-pre-line">{message.body}</div>}
        {message.activity_details && Object.keys(message.activity_details).length > 0 && (
          <div className="mt-1 text-[11px] text-neutral-500">
            {Object.entries(message.activity_details).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
        {(message.media || []).length > 0 && (
          <div className={`mt-2 grid gap-1 ${message.media.length > 1 ? "grid-cols-2" : ""}`}>
            {message.media.map((src, i) => (
              <div key={i} className="relative group">
                <img src={src} alt="" className="rounded max-h-56 object-cover w-full border" />
                <button onClick={() => downloadPhoto(src, i)}
                        data-testid={`photo-download-${message.id}-${i}`}
                        className="absolute top-1 right-1 bg-white/95 rounded p-1 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Bewaar foto">
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] text-neutral-400 mt-1 px-0.5 text-right">
          {new Date(message.created_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

function AIAssistBlock({ kinderen, onFill, defaultKindId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [multiOpen, setMultiOpen] = useState(false);
  const [multiTranscript, setMultiTranscript] = useState("");
  const [form, setForm] = useState({
    kind_naam: "",
    leeftijd: "",
    stemming: "",
    eten: "",
    slapen: "",
    toilet: "",
    activiteiten: "",
    positief: "",
    bijzonderheden: "",
    belangrijke_mededeling: "",
    gewenste_taal: "beide",
    gewenste_lengte: "kort",
    ruwe_notities: "",
  });

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/schriftje/ai-assist", { ...form, datum: localToday() });
      setResult(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleTranscribed = (text) => {
    if (!text || !text.trim()) {
      toast.error("Lege opname — geen tekst herkend. Probeer opnieuw.");
      return;
    }
    // Zet transcript in ruwe_notities
    setForm(f => ({
      ...f,
      ruwe_notities: (f.ruwe_notities ? f.ruwe_notities + " " : "") + text,
    }));
    // Automatische multi-kind detectie: als 2+ bekende kindnamen in transcript
    const known = countKnownKinderenInTranscript(text, kinderen);
    if (known >= 2) {
      setMultiTranscript(text);
      setMultiOpen(true);
      toast.info(`${known} kinderen herkend — controleer per kind`);
    }
  };

  const apply = () => {
    if (!result) return;
    onFill(result.nederlands || "", result.english || "");
    if (result.belangrijke_mededeling) toast.info("Belangrijke mededeling: zie de tekst.");
    setOpen(false);
  };

  return (
    <div className="mb-4 rounded-md border border-[color:var(--terracotta)]/30 bg-[color:var(--terracotta)]/5 p-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="ai-assist-toggle"
        className="flex items-center gap-2 text-sm font-semibold text-[color:var(--terracotta)] hover:text-[color:var(--terracotta-hover)] transition-colors"
      >
        <Sparkles className="h-4 w-4" />
        AI-schriftjesassistent {open ? "verbergen" : "openen"}
        <span className="ml-2 text-[10px] font-normal uppercase tracking-widest text-neutral-500">Webster · Claude Sonnet</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2" data-testid="ai-assist-panel">
          <p className="text-xs text-neutral-600">Vul steekwoorden in of spreek ze in — de AI schrijft geen dingen die er niet in staan.</p>
          <div className="flex items-center gap-2">
            <VoiceRecorder onText={handleTranscribed} />
            <span className="text-[11px] text-neutral-500">Spreek naam, activiteiten, eten, slaap, bijzonderheden — Whisper NL</span>
          </div>
          {kinderen?.length > 0 && form.ruwe_notities && countKnownKinderenInTranscript(form.ruwe_notities, kinderen) >= 2 && (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs" data-testid="multi-kind-cta">
              <Sparkles className="h-3 w-3 text-amber-700" />
              <span className="flex-1 text-amber-900">
                Meerdere kinderen herkend — open per-kind controle
              </span>
              <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-400"
                      onClick={() => { setMultiTranscript(form.ruwe_notities); setMultiOpen(true); }}
                      data-testid="multi-kind-open-btn">
                Openen
              </Button>
            </div>
          )}
          {form.ruwe_notities && (
            <div className="rounded-md bg-white border border-neutral-200 p-2" data-testid="ai-transcript">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">Transcript (bewerkbaar)</div>
              <Textarea rows={3} value={form.ruwe_notities} onChange={e => setForm({ ...form, ruwe_notities: e.target.value })} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Naam kind (bv. Sofia)" value={form.kind_naam} onChange={e => setForm({ ...form, kind_naam: e.target.value })} data-testid="ai-input-naam" />
            <Input placeholder="Leeftijd (bv. 2 jaar)" value={form.leeftijd} onChange={e => setForm({ ...form, leeftijd: e.target.value })} />
            <Input placeholder="Stemming" value={form.stemming} onChange={e => setForm({ ...form, stemming: e.target.value })} />
            <Input placeholder="Eten & drinken" value={form.eten} onChange={e => setForm({ ...form, eten: e.target.value })} />
            <Input placeholder="Slapen (bv. 12.45-14.15)" value={form.slapen} onChange={e => setForm({ ...form, slapen: e.target.value })} />
            <Input placeholder="Toilet / luiers" value={form.toilet} onChange={e => setForm({ ...form, toilet: e.target.value })} />
          </div>
          <Textarea placeholder="Activiteiten (bv. buiten blaadjes, geverfd met sponsjes)" rows={2}
            value={form.activiteiten} onChange={e => setForm({ ...form, activiteiten: e.target.value })}
            data-testid="ai-input-activiteiten" />
          <Textarea placeholder="Positief opgevallen" rows={1}
            value={form.positief} onChange={e => setForm({ ...form, positief: e.target.value })} />
          <Textarea placeholder="Bijzonderheden" rows={1}
            value={form.bijzonderheden} onChange={e => setForm({ ...form, bijzonderheden: e.target.value })} />
          <Textarea placeholder="Belangrijke mededeling (medisch/incident)" rows={1}
            value={form.belangrijke_mededeling} onChange={e => setForm({ ...form, belangrijke_mededeling: e.target.value })} />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Select value={form.gewenste_taal} onValueChange={v => setForm({ ...form, gewenste_taal: v })}>
                <SelectTrigger className="w-32" data-testid="ai-input-taal"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Nederlands">Nederlands</SelectItem>
                  <SelectItem value="Engels">Engels</SelectItem>
                  <SelectItem value="beide">Beide</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.gewenste_lengte} onValueChange={v => setForm({ ...form, gewenste_lengte: v })}>
                <SelectTrigger className="w-36" data-testid="ai-input-lengte"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="kort">Kort (60-80w)</SelectItem>
                  <SelectItem value="gemiddeld">Gemiddeld (120-160w)</SelectItem>
                  <SelectItem value="uitgebreid">Uitgebreid (220-280w)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={run} disabled={loading} data-testid="ai-generate-btn"
              style={{ background: "var(--terracotta)" }} className="text-white">
              <Sparkles className="h-3 w-3 mr-1" />
              {loading ? "Schrijven…" : "Genereer schriftje"}
            </Button>
          </div>

          {result && (
            <div className="mt-3 space-y-3" data-testid="ai-result">
              {result.belangrijke_mededeling && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3">
                  <div className="flex items-center gap-2 text-red-700 font-semibold text-xs uppercase tracking-widest mb-1">
                    <AlertTriangle className="h-3 w-3" /> Belangrijke mededeling
                  </div>
                  <div className="text-sm text-red-800 whitespace-pre-line">{result.belangrijke_mededeling}</div>
                </div>
              )}
              {result.nederlands && (
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">Nederlands</div>
                  <div className="text-sm text-neutral-800 whitespace-pre-line">{result.nederlands}</div>
                </div>
              )}
              {result.english && (
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">English</div>
                  <div className="text-sm text-neutral-800 whitespace-pre-line">{result.english}</div>
                </div>
              )}
              {result.activiteiten && result.activiteiten.length > 0 && (
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Herkende activiteiten (intern)</div>
                  <div className="space-y-2">
                    {result.activiteiten.map((a, i) => (
                      <div key={i} className="text-xs">
                        <div className="font-semibold text-neutral-800">{a.activiteit}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(a.categorieen || []).map(c => <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>)}
                          {(a.ontwikkelingsgebieden || []).map(o => <Badge key={o} variant="outline" className="text-[10px] bg-[color:var(--sage-100)] text-[color:var(--sage)] border-transparent">{o}</Badge>)}
                        </div>
                        {a.toelichting && <div className="text-neutral-600 mt-1">{a.toelichting}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {result.waarschuwingen && result.waarschuwingen.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  {result.waarschuwingen.map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={apply} data-testid="ai-apply-btn" style={{ background: "var(--sage)" }} className="text-white">
                  Gebruik dit in schriftje
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <MultiKindSchriftjeDialog
        open={multiOpen}
        onClose={() => setMultiOpen(false)}
        transcript={multiTranscript}
        kinderen={kinderen || []}
        defaultDatum={localToday()}
      />
    </div>
  );
}
